import React from "react";
import { Box } from "@mui/material";
import { Theme } from "../../../../providers/ThemeProvider";
import useTheme from "../../../../common/hooks/useTheme";

type Props = {
  companyName: string;
  sectionName: string;
  pageIndex: number;
  children?: React.ReactNode;
};

const PageLayout = ({
  companyName,
  sectionName,
  pageIndex,
  children,
}: Props) => {
  const theme: Theme = useTheme();

  // Format page number to always be 2 digits (e.g., 01, 02, 18)
  const formattedPageNumber = String(pageIndex + 1).padStart(2, "0");

  return (
    <Box sx={styles.pageContainer(theme)}>
      {/* Header */}
      <Box sx={styles.header(theme)}>
        <Box component="p" sx={styles.companyName(theme)}>
          {companyName}
        </Box>
        <Box component="p" sx={styles.sectionName(theme)}>
          {sectionName}
        </Box>
      </Box>

      {/* Content Area */}
      <Box sx={styles.content}>{children}</Box>

      {/* Footer */}
      <Box sx={styles.footer}>
        <Box sx={styles.footerLine(theme)} />
        <Box component="p" sx={styles.pageNumber(theme)}>
          {formattedPageNumber}
        </Box>
      </Box>
    </Box>
  );
};

export default PageLayout;

const styles = {
  pageContainer: (theme: Theme) => ({
    width: "595px", // A4 width in points for print/PDF
    height: "843px", // A4 height in points for print/PDF
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    position: "relative" as "relative",
    display: "flex",
    flexDirection: "column" as "column",
    borderBottom: `1px solid ${theme.colorTokens.N[30]}`,
  }),
  header: (theme: Theme) => ({
    borderBottom: `0.5px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 18px",
  }),
  companyName: (theme: Theme) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "11px",
    lineHeight: "normal",
    color: theme.colorTokens.P[90], // #04439C
    margin: 0,
  }),
  sectionName: (theme: Theme) => ({
    fontFamily: "var(--font-Light)",
    fontSize: "9px",
    lineHeight: "normal",
    color: theme.colorTokens.N[80], // #6D7075
    textAlign: "right" as "right",
    margin: 0,
  }),
  content: {
    height: "763px",
    overflow: "hidden",
  },
  footer: {
    display: "flex",
    gap: "24px",
    alignItems: "center",
    padding: "12px 18px",
  },
  footerLine: (theme: Theme) => ({
    flex: 1,
    height: "0.5px",
    backgroundColor: theme.colors_ds.NEUTRAL_BORDER_WEAK, // #E1E3E5
  }),
  pageNumber: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "11px",
    lineHeight: "normal",
    color: theme.colorTokens.P[90], // #04439C
    textAlign: "right" as "right",
    margin: 0,
  }),
};
